
import { ReactNode, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from './Button';
import { Checkbox } from './Checkbox';

export type SortDirection = 'asc' | 'desc' | null;

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  render?: (row: T, index: number) => ReactNode;
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  isLoading?: boolean;
  emptyMessage?: string;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSort?: (column: string, direction: SortDirection) => void;
  // Pagination
  page?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  className?: string;
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  isLoading = false,
  emptyMessage = 'No data available',
  selectable = false,
  selectedKeys = new Set(),
  onSelectionChange,
  sortColumn,
  sortDirection,
  onSort,
  page = 1,
  pageSize = 10,
  totalItems,
  onPageChange,
  className,
}: TableProps<T>) {
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Set<string>>(
    selectedKeys
  );

  const effectiveSelectedKeys = onSelectionChange
    ? selectedKeys
    : internalSelectedKeys;
  const setEffectiveSelectedKeys = onSelectionChange
    ? onSelectionChange
    : setInternalSelectedKeys;

  const handleSelectAll = useCallback(() => {
    if (effectiveSelectedKeys.size === data.length) {
      setEffectiveSelectedKeys(new Set());
    } else {
      setEffectiveSelectedKeys(new Set(data.map(keyExtractor)));
    }
  }, [data, effectiveSelectedKeys.size, keyExtractor, setEffectiveSelectedKeys]);

  const handleSelectRow = useCallback(
    (key: string) => {
      const newKeys = new Set(effectiveSelectedKeys);
      if (newKeys.has(key)) {
        newKeys.delete(key);
      } else {
        newKeys.add(key);
      }
      setEffectiveSelectedKeys(newKeys);
    },
    [effectiveSelectedKeys, setEffectiveSelectedKeys]
  );

  const handleSort = useCallback(
    (column: string) => {
      if (!onSort) return;
      let newDirection: SortDirection = 'asc';
      if (sortColumn === column) {
        if (sortDirection === 'asc') newDirection = 'desc';
        else if (sortDirection === 'desc') newDirection = null;
      }
      onSort(column, newDirection);
    },
    [onSort, sortColumn, sortDirection]
  );

  const totalPages = totalItems ? Math.ceil(totalItems / pageSize) : 1;
  const showPagination = totalItems !== undefined && totalPages > 1;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={cn('overflow-x-auto', className)}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {selectable && (
                <th className="px-4 py-3 w-12">
                  <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </th>
              )}
              {columns.map((col) => (
                <th key={col.key} className="px-6 py-3">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {[...Array(5)].map((_, i) => (
              <tr key={i}>
                {selectable && (
                  <td className="px-4 py-4">
                    <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className="px-6 py-4">
                    <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className={cn('overflow-x-auto', className)}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {selectable && <th className="px-4 py-3 w-12" />}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  style={{ width: col.width }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
              >
                {emptyMessage}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {selectable && (
              <th className="px-4 py-3 w-12">
                <Checkbox
                  checked={effectiveSelectedKeys.size === data.length}
                  onChange={handleSelectAll}
                  aria-label="Select all"
                  label="Select all"
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider',
                  col.sortable && 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
                style={{ width: col.width }}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
              >
                <div className="flex items-center gap-1">
                  {col.header}
                  {col.sortable && (
                    <span className="ml-1">
                      {sortColumn === col.key ? (
                        sortDirection === 'asc' ? (
                          <SortAscIcon />
                        ) : sortDirection === 'desc' ? (
                          <SortDescIcon />
                        ) : (
                          <SortIcon />
                        )
                      ) : (
                        <SortIcon />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((row, index) => {
            const key = keyExtractor(row);
            const isSelected = effectiveSelectedKeys.has(key);
            return (
              <tr
                key={key}
                className={cn(
                  'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
                  isSelected && 'bg-primary-50 dark:bg-primary-900/20'
                )}
              >
                {selectable && (
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectRow(key)}
                      aria-label={`Select row ${index + 1}`}
                      label={`Select row ${index + 1}`}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                  >
                    {col.render
                      ? col.render(row, index)
                      : String((row as Record<string, unknown>)[col.key] ?? '')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {showPagination && (
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {(page - 1) * pageSize + 1} to{' '}
            {Math.min(page * pageSize, totalItems)} of {totalItems} results
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange?.(page + 1)}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Sort icons
function SortIcon() {
  return (
    <svg
      className="w-4 h-4 text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
      />
    </svg>
  );
}

function SortAscIcon() {
  return (
    <svg
      className="w-4 h-4 text-primary-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 15l7-7 7 7"
      />
    </svg>
  );
}

function SortDescIcon() {
  return (
    <svg
      className="w-4 h-4 text-primary-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}
